import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, MessageSquareText, Star, Users } from 'lucide-react';
import {
  Carte,
  Chargement,
  Compteur,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../ui/Primitives';
import { BarreFiltres } from '../ui/BarreFiltres';
import { ChampDate } from '../ui/ChampDate';
import { ChampFiltre, ChampRecherche } from '../ui/Onglets';
import { ErreurApi } from '../../lib/api';
import { useToast } from '../ui/useToast';
import { useTaillePage } from '../../lib/useTaillePage';
import { useValeurDifferee } from '../../lib/useRecherche';
import {
  FILTRES_SATISFACTION_PAR_DEFAUT,
  texteNps,
  useTableauSatisfaction,
  useTraiterAlerteQualite,
  type AlertesQualite as DonneesAlertes,
  type AxeSatisfaction,
  type ChoixPeriodeSatisfaction,
  type CommentaireSatisfaction,
  type FamilleQuestionnaire,
  type FiltresTableauSatisfaction,
  type LigneGroupeSatisfaction,
  type PartSatisfaction,
  type PointEvolution,
  type TableauSatisfaction as DonneesTableau,
} from '../../lib/useQuestionnaires';

// ---------------------------------------------------------------------------
// Mise en forme
// ---------------------------------------------------------------------------

function surCinq(valeur: number | null): string {
  return valeur === null ? '—' : `${valeur.toFixed(1).replace('.', ',')} / 5`;
}

function enPourcent(valeur: number | null): string {
  return valeur === null ? '—' : `${valeur} %`;
}

function jourFrancais(jour: string): string {
  return jour.split('-').reverse().join('/');
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

/** « +0,3 », « −1 pt » : l'écart que l'API a calculé, signé. */
function texteEcart(ecart: number | null, unite: 'note' | 'points'): string | null {
  if (ecart === null) return null;
  const valeur = unite === 'note' ? Math.abs(ecart).toFixed(1).replace('.', ',') : String(Math.abs(ecart));
  const signe = ecart > 0 ? '+' : ecart < 0 ? '−' : '±';
  return unite === 'note' ? `${signe}${valeur}` : `${signe}${valeur} pt${Math.abs(ecart) > 1 ? 's' : ''}`;
}

function EcartPastille({ ecart, unite }: { ecart: number | null; unite: 'note' | 'points' }): JSX.Element | null {
  const texte = texteEcart(ecart, unite);
  if (texte === null || ecart === null) return null;
  return <Pastille texte={texte} ton={ecart > 0 ? 'success' : ecart < 0 ? 'danger' : 'neutral'} />;
}

function cleRecherche(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

function Etoiles({ valeur, taille = 'h-4 w-4' }: { valeur: number | null; taille?: string }): JSX.Element {
  const pleines = valeur === null ? 0 : Math.round(valeur);
  return (
    <span className="flex shrink-0 gap-0.5" aria-hidden>
      {[1, 2, 3, 4, 5].map((rang) => (
        <Star
          key={rang}
          className={`${taille} ${rang <= pleines ? 'fill-amber-400 text-amber-400' : 'fill-gray-200 text-gray-200'}`}
        />
      ))}
    </span>
  );
}

function Barre({ part }: { part: number }): JSX.Element {
  return (
    <div className="progress-track h-1.5 w-full overflow-hidden rounded-full">
      <div className="progress-bar h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, part))}%` }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Écran
// ---------------------------------------------------------------------------

/**
 * Tableau de bord de satisfaction (TF-433) : questionnaires à chaud
 * (satisfaction à 2 mois, 6 mois, fin de parcours) et à froid (insertion à 3
 * et 6 mois après l'examen), moyennes et évolution mois par mois, résultats
 * par formation, formateur référent et promotion, commentaires.
 *
 * Tout vient de `GET /api/questionnaires/tableau-de-bord`, qui borne aussi ce
 * que chacun lit : le formateur ses apprenants référents, sans les noms. Le NPS
 * et les alertes de note basse sont TF-434 ; l'écran se relit chaque minute.
 */
export function TableauSatisfaction({
  site,
  onVoirReponse,
}: {
  site: string;
  /** Ouvre un questionnaire répondu en entier (`?reponse=<id>`). */
  onVoirReponse: (id: string) => void;
}): JSX.Element {
  const [filtres, setFiltres] = useState<FiltresTableauSatisfaction>(FILTRES_SATISFACTION_PAR_DEFAUT);
  const { data, isLoading, error, isFetching } = useTableauSatisfaction(site, filtres);
  const modifie = JSON.stringify(filtres) !== JSON.stringify(FILTRES_SATISFACTION_PAR_DEFAUT);

  return (
    <div className="space-y-6">
      <Filtres
        filtres={filtres}
        donnees={data}
        onChanger={setFiltres}
        onReinitialiser={modifie ? () => setFiltres(FILTRES_SATISFACTION_PAR_DEFAUT) : null}
      />

      {isLoading && <Chargement />}
      {error instanceof ErreurApi && <MessageErreur message={error.message} />}
      {filtres.periode === 'LIBRE' && (!filtres.du || !filtres.au) && !data && (
        <p className="rounded-2xl bg-white p-4 text-sm text-codialis-gray shadow-card">
          Indiquez le premier et le dernier jour de la période.
        </p>
      )}

      {data && (
        <div className={`space-y-6 transition-opacity ${isFetching ? 'opacity-70' : ''}`}>
          <Lecture donnees={data} />
          <ChiffresCles donnees={data} />
          {data.alertes && <AlertesQualite alertes={data.alertes} onVoirReponse={onVoirReponse} />}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="min-w-0 lg:col-span-2">
              <Criteres donnees={data} />
            </div>
            <div className="min-w-0 space-y-6">
              <CarteNps donnees={data} />
              <AFroid donnees={data} />
            </div>
          </div>
          <Evolution donnees={data} />
          <ParAxe donnees={data} />
          <Commentaires donnees={data} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filtres
// ---------------------------------------------------------------------------

function Filtres({
  filtres,
  donnees,
  onChanger,
  onReinitialiser,
}: {
  filtres: FiltresTableauSatisfaction;
  donnees: DonneesTableau | undefined;
  onChanger: (filtres: FiltresTableauSatisfaction) => void;
  onReinitialiser: (() => void) | null;
}): JSX.Element {
  const options = donnees?.options;
  const changer = (valeurs: Partial<FiltresTableauSatisfaction>): void => onChanger({ ...filtres, ...valeurs });
  // Le formateur ne lit que ses apprenants référents : il n'a pas d'autre formateur à choisir.
  const choixFormateur = donnees?.lecture.perimetre !== 'REFERENT';

  return (
    <BarreFiltres onReinitialiser={onReinitialiser}>
      <ChampFiltre
        valeur={filtres.periode}
        onChange={(valeur) => {
          const periode = valeur as ChoixPeriodeSatisfaction;
          // Des dates libres partent de la période affichée.
          changer({
            periode,
            du: periode === 'LIBRE' && !filtres.du ? (donnees?.periode.du ?? '') : filtres.du,
            au: periode === 'LIBRE' && !filtres.au ? (donnees?.periode.arreteeAu ?? '') : filtres.au,
          });
        }}
        etiquette="Période"
      >
        {(options?.periodes ?? [{ valeur: 'DOUZE_MOIS' as ChoixPeriodeSatisfaction, libelle: '12 derniers mois' }]).map(
          (option) => (
            <option key={option.valeur} value={option.valeur}>
              {option.libelle}
            </option>
          ),
        )}
      </ChampFiltre>
      {filtres.periode === 'LIBRE' && (
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <ChampDate libelle="Du" valeur={filtres.du} max={filtres.au || undefined} onChange={(du) => changer({ du })} />
          <ChampDate libelle="au" valeur={filtres.au} min={filtres.du || undefined} onChange={(au) => changer({ au })} />
        </div>
      )}
      <ChampFiltre valeur={filtres.questionnaire} onChange={(questionnaire) => changer({ questionnaire })} etiquette="Questionnaire">
        <option value="">Tous les questionnaires</option>
        {(['CHAUD', 'FROID'] as FamilleQuestionnaire[]).map((famille) => {
          const liste = options?.questionnaires.filter((option) => option.famille === famille) ?? [];
          return liste.length === 0 ? null : (
            <optgroup key={famille} label={liste[0]?.libelleFamille}>
              {liste.map((option) => (
                <option key={option.valeur} value={option.valeur}>
                  {option.libelle}
                </option>
              ))}
            </optgroup>
          );
        })}
      </ChampFiltre>
      <ChampFiltre valeur={filtres.formation} onChange={(formation) => changer({ formation })} etiquette="Formation">
        <option value="">Toutes les formations</option>
        {options?.formations.map((groupe) => (
          <option key={groupe.cle} value={groupe.cle}>
            {groupe.libelle}
          </option>
        ))}
      </ChampFiltre>
      {choixFormateur && (
        <ChampFiltre valeur={filtres.formateur} onChange={(formateur) => changer({ formateur })} etiquette="Formateur référent">
          <option value="">Tous les formateurs</option>
          {options?.formateurs.map((groupe) => (
            <option key={groupe.cle} value={groupe.cle}>
              {groupe.libelle}
            </option>
          ))}
        </ChampFiltre>
      )}
      <ChampFiltre valeur={filtres.promotion} onChange={(promotion) => changer({ promotion })} etiquette="Promotion">
        <option value="">Toutes les promotions</option>
        {options?.promotions.map((groupe) => (
          <option key={groupe.cle} value={groupe.cle}>
            {groupe.siteCode ? `${groupe.libelle} · ${groupe.siteCode}` : groupe.libelle}
          </option>
        ))}
      </ChampFiltre>
    </BarreFiltres>
  );
}

function Lecture({ donnees }: { donnees: DonneesTableau }): JSX.Element {
  const { periode, lecture } = donnees;
  return (
    <div className="space-y-2">
      <p className="text-sm text-codialis-gray">
        Réponses reçues{' '}
        {periode.choix === 'DOUZE_MOIS'
          ? `sur les 12 derniers mois, ${periode.libelle}`
          : periode.choix === 'LIBRE'
            ? periode.libelle
            : `en ${periode.du.slice(0, 4)}`}
        {periode.arreteeAu !== periode.au ? `, jusqu’au ${jourFrancais(periode.arreteeAu)}` : ''} · comparées à la période{' '}
        {periode.comparaison.libelle}.
      </p>
      {lecture.message && (
        <p className="flex items-start gap-2 rounded-xl bg-codialis-sand p-3 text-xs text-codialis-navy">
          <Users className="mt-0.5 h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
          <span className="min-w-0">{lecture.message}</span>
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chiffres clés
// ---------------------------------------------------------------------------

function ChiffresCles({ donnees }: { donnees: DonneesTableau }): JSX.Element {
  const { chaud, froid, familles } = donnees;
  const { annees } = donnees.periode.comparaison;
  const detailAvecEcart = (base: string, ecart: string | null): string =>
    ecart ? `${base} · ${ecart} sur ${annees > 1 ? `${annees} ans` : 'un an'}` : base;
  const litChaud = familles.includes('CHAUD');
  const litFroid = familles.includes('FROID');
  const horsFiltre = 'Hors du questionnaire choisi';
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Compteur
        libelle="À chaud · moyenne"
        valeur={litChaud ? surCinq(chaud.moyenne) : '—'}
        ton="blue"
        detail={
          litChaud
            ? detailAvecEcart(pluriel(chaud.reponses, 'réponse'), texteEcart(chaud.ecartMoyenne, 'note'))
            : horsFiltre
        }
      />
      <Compteur
        libelle="NPS"
        valeur={litChaud ? texteNps(chaud.nps.score) : '—'}
        ton="mint"
        detail={
          litChaud
            ? chaud.nps.repondants === 0
              ? 'Aucune recommandation notée de 0 à 10 sur la période'
              : detailAvecEcart(
                  `${pluriel(chaud.nps.promoteurs, 'promoteur')} · ${pluriel(chaud.nps.detracteurs, 'détracteur')} sur ${chaud.nps.repondants}`,
                  texteEcart(chaud.ecartNps, 'points'),
                )
            : horsFiltre
        }
      />
      <Compteur
        libelle="Taux de réponse à chaud"
        valeur={litChaud ? enPourcent(chaud.tauxReponse) : '—'}
        detail={
          litChaud
            ? chaud.envoyes === 0
              ? 'Aucun questionnaire envoyé sur la période'
              : `${chaud.repondus} sur ${pluriel(chaud.envoyes, 'questionnaire envoyé', 'questionnaires envoyés')}`
            : horsFiltre
        }
      />
      <Compteur
        libelle="À froid · en emploi"
        valeur={litFroid ? enPourcent(froid.enEmploi.part) : '—'}
        ton="warm"
        detail={
          litFroid
            ? froid.enEmploi.repondants === 0
              ? 'Aucune réponse sur la période'
              : detailAvecEcart(
                  `${froid.enEmploi.oui} sur ${pluriel(froid.enEmploi.repondants, 'répondant')}`,
                  texteEcart(froid.ecarts.enEmploi, 'points'),
                )
            : horsFiltre
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// À chaud : moyennes par critère
// ---------------------------------------------------------------------------

const COURT_QUESTIONNAIRE: Record<string, string> = {
  SATISFACTION_2M: '2 mois',
  SATISFACTION_6M: '6 mois',
  SATISFACTION_FIN: 'fin de parcours',
  INSERTION_3M: '3 mois après l’examen',
  INSERTION_6M: '6 mois après l’examen',
};

function Criteres({ donnees }: { donnees: DonneesTableau }): JSX.Element {
  const { chaud } = donnees;
  return (
    <Carte titre="À chaud · moyennes par critère" actions={<span className="text-xs text-codialis-gray">sur 5</span>} sansBordure>
      <div className="space-y-4 p-4 sm:p-6">
        {!donnees.familles.includes('CHAUD') ? (
          <p className="text-sm text-codialis-gray">Le questionnaire choisi est un questionnaire à froid.</p>
        ) : chaud.reponses === 0 ? (
          <p className="text-sm text-codialis-gray">Aucune réponse à un questionnaire de satisfaction sur la période.</p>
        ) : (
          chaud.criteres.map((critere) => (
            <div key={critere.id} className="min-w-0">
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-codialis-navy">{critere.libelle}</div>
                  <div className="text-[11px] text-codialis-gray">
                    {critere.questionnaires.map((type) => COURT_QUESTIONNAIRE[type] ?? type).join(' · ')} ·{' '}
                    {pluriel(critere.notes, 'note')}
                  </div>
                </div>
                <span className="flex shrink-0 items-center gap-2">
                  <Etoiles valeur={critere.moyenne} />
                  <span className="text-sm font-bold text-codialis-navy">
                    {critere.moyenne === null ? '—' : critere.moyenne.toFixed(1).replace('.', ',')}
                  </span>
                  <EcartPastille ecart={critere.ecart} unite="note" />
                </span>
              </div>
              <Barre part={critere.moyenne === null ? 0 : (critere.moyenne / 5) * 100} />
            </div>
          ))
        )}
      </div>
    </Carte>
  );
}

// ---------------------------------------------------------------------------
// Net Promoter Score (TF-434)
// ---------------------------------------------------------------------------

function CarteNps({ donnees }: { donnees: DonneesTableau }): JSX.Element {
  const { nps, recommandation } = donnees.chaud;
  const parts = nps.parts;
  return (
    <Carte titre="Net Promoter Score" actions={<span className="text-xs text-codialis-gray">à chaud</span>} sansBordure>
      <div className="space-y-4 p-4 sm:p-6">
        {!donnees.familles.includes('CHAUD') ? (
          <p className="text-sm text-codialis-gray">Le questionnaire choisi est un questionnaire à froid.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
              <span className="font-display text-4xl font-extrabold text-codialis-navy">{texteNps(nps.score)}</span>
              <span className="pb-1 text-xs text-codialis-gray">
                {nps.repondants === 0 ? 'aucune note sur la période' : pluriel(nps.repondants, 'note')}
              </span>
              <span className="pb-1">
                <EcartPastille ecart={donnees.chaud.ecartNps} unite="points" />
              </span>
            </div>
            {parts && (
              <>
                <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden>
                  <div className="h-full bg-codialis-mint" style={{ width: `${parts.promoteurs}%` }} />
                  <div className="h-full bg-gray-300" style={{ width: `${parts.passifs}%` }} />
                  <div className="h-full bg-red-400" style={{ width: `${parts.detracteurs}%` }} />
                </div>
                <dl className="grid grid-cols-3 gap-2 text-center text-[11px] text-codialis-gray">
                  <div className="min-w-0">
                    <dd className="text-lg font-bold text-codialis-navy">{parts.promoteurs} %</dd>
                    <dt>
                      <span className="block">Promoteurs</span>
                      <span className="block">9-10</span>
                    </dt>
                  </div>
                  <div className="min-w-0">
                    <dd className="text-lg font-bold text-codialis-navy">{parts.passifs} %</dd>
                    <dt>
                      <span className="block">Passifs</span>
                      <span className="block">7-8</span>
                    </dt>
                  </div>
                  <div className="min-w-0">
                    <dd className="text-lg font-bold text-red-600">{parts.detracteurs} %</dd>
                    <dt>
                      <span className="block">Détracteurs</span>
                      <span className="block">0-6</span>
                    </dt>
                  </div>
                </dl>
              </>
            )}
            <p className="text-[11px] text-codialis-gray">
              Recommandation notée de 0 à 10 : part des promoteurs moins part des détracteurs, de −100 à +100.
            </p>
            {recommandation.repondants > 0 && (
              <p className="rounded-xl bg-codialis-sand p-3 text-xs text-codialis-navy">
                Questionnaires envoyés avant la note de 0 à 10 : {recommandation.oui} sur {recommandation.repondants}{' '}
                recommandent ({enPourcent(recommandation.part)}).
              </p>
            )}
          </>
        )}
      </div>
    </Carte>
  );
}

function AlertesQualite({
  alertes,
  onVoirReponse,
}: {
  alertes: DonneesAlertes;
  onVoirReponse: (id: string) => void;
}): JSX.Element {
  const traiter = useTraiterAlerteQualite();
  const notifier = useToast();
  return (
    <Carte
      titre="Alertes qualité"
      actions={<Pastille texte={String(alertes.ouvertes)} ton={alertes.ouvertes > 0 ? 'danger' : 'neutral'} />}
      sansBordure
    >
      <p className="border-b border-gray-100 px-4 py-2.5 text-xs text-codialis-gray sm:px-6">
        Réponses qui notent au moins un critère sous {alertes.seuil}/5, à traiter · toutes périodes, sur les antennes
        choisies
      </p>
      {alertes.lignes.length === 0 ? (
        <p className="flex items-center gap-2 p-4 text-sm text-codialis-gray sm:p-6">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-codialis-mint" aria-hidden />
          Aucune note basse à traiter.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {alertes.lignes.map((alerte) => (
            <li key={alerte.id} className="flex flex-col gap-3 px-4 py-3.5 sm:px-6 md:flex-row md:items-start">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" aria-hidden />
                  <span className="text-sm font-semibold text-codialis-navy">{alerte.apprenant ?? 'Apprenant retiré'}</span>
                  {alerte.siteCode && <PastilleSite site={alerte.siteCode} />}
                  {alerte.recommandation !== null && (
                    <Pastille
                      texte={`Recommandation ${alerte.recommandation}/10`}
                      ton={alerte.recommandation >= 9 ? 'success' : alerte.recommandation >= 7 ? 'neutral' : 'warning'}
                    />
                  )}
                </div>
                <div className="text-xs text-codialis-gray">
                  {[
                    alerte.formation,
                    alerte.questionnaire,
                    alerte.repondLe ? `répondu le ${jourFrancais(alerte.repondLe)}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                {alerte.notes.length > 0 ? (
                  <ul className="space-y-0.5">
                    {alerte.notes.map((note) => (
                      <li key={note.libelle} className="flex min-w-0 gap-2 text-sm text-codialis-navy">
                        <span className="shrink-0 font-bold text-red-600">{note.note}/5</span>
                        <span className="min-w-0 break-words">{note.libelle}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="break-words text-sm text-codialis-navy">{alerte.message}</p>
                )}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {alerte.envoiId && (
                  <button
                    type="button"
                    onClick={() => onVoirReponse(alerte.envoiId as string)}
                    className="min-h-[40px] rounded-xl border border-gray-200 px-3 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
                  >
                    Voir la réponse
                  </button>
                )}
                {alertes.peutStatuer && (
                  <button
                    type="button"
                    onClick={() =>
                      traiter.mutate(alerte.id, { onSuccess: () => notifier('Alerte marquée traitée.') })
                    }
                    disabled={traiter.isPending}
                    className="min-h-[40px] rounded-xl bg-codialis-navy px-3 text-xs font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-50"
                  >
                    Marquer traitée
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {traiter.error instanceof ErreurApi && (
        <div className="px-4 pb-4 sm:px-6">
          <MessageErreur message={traiter.error.message} />
        </div>
      )}
      {alertes.ouvertes > alertes.lignes.length && (
        <p className="border-t border-gray-100 px-4 py-3 text-[11px] text-codialis-gray sm:px-6">
          {alertes.ouvertes - alertes.lignes.length} autre(s) alerte(s) ouverte(s), à lire dans le module d’alertes.
        </p>
      )}
    </Carte>
  );
}

// ---------------------------------------------------------------------------
// À froid
// ---------------------------------------------------------------------------

function TuileFroid({
  libelle,
  part,
  ecart,
}: {
  libelle: string;
  part: PartSatisfaction;
  ecart: number | null;
}): JSX.Element {
  return (
    <div className="min-w-0 rounded-xl bg-gray-50 p-4 text-center">
      <div className="font-display text-2xl font-extrabold text-codialis-navy">{enPourcent(part.part)}</div>
      <div className="mt-1 text-sm font-medium text-codialis-navy">{libelle}</div>
      <div className="mt-0.5 flex flex-wrap items-center justify-center gap-1.5 text-xs text-codialis-gray">
        <span>{part.repondants === 0 ? 'aucun répondant' : `${part.oui} sur ${part.repondants}`}</span>
        <EcartPastille ecart={ecart} unite="points" />
      </div>
    </div>
  );
}

function AFroid({ donnees }: { donnees: DonneesTableau }): JSX.Element {
  const { froid } = donnees;
  return (
    <Carte titre="À froid · insertion" sansBordure>
      <div className="space-y-4 p-4 sm:p-6">
        {!donnees.familles.includes('FROID') ? (
          <p className="text-sm text-codialis-gray">Le questionnaire choisi est un questionnaire à chaud.</p>
        ) : (
          <>
            <p className="text-sm text-codialis-gray">
              Questionnaires à 3 et 6 mois après l’examen · {pluriel(froid.reponses, 'réponse')}
              {froid.envoyes > 0 ? ` · taux de réponse ${enPourcent(froid.tauxReponse)}` : ''}
            </p>
            {froid.reponses === 0 ? (
              <p className="text-sm text-codialis-gray">Aucune réponse à un questionnaire d’insertion sur la période.</p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                <TuileFroid libelle="En emploi" part={froid.enEmploi} ecart={froid.ecarts.enEmploi} />
                <TuileFroid libelle="Emploi en lien avec la formation" part={froid.emploiEnLien} ecart={froid.ecarts.emploiEnLien} />
                <TuileFroid libelle="Diplôme ou titre obtenu" part={froid.diplomeObtenu} ecart={froid.ecarts.diplomeObtenu} />
                <TuileFroid
                  libelle="Employé(e) par l’entreprise d’alternance"
                  part={froid.gardeParEntreprise}
                  ecart={froid.ecarts.gardeParEntreprise}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Carte>
  );
}

// ---------------------------------------------------------------------------
// Évolution
// ---------------------------------------------------------------------------

type Mesure = 'MOYENNE' | 'NPS' | 'EN_EMPLOI';

const MESURES: { cle: Mesure; libelle: string; famille: FamilleQuestionnaire }[] = [
  { cle: 'MOYENNE', libelle: 'Moyenne à chaud', famille: 'CHAUD' },
  { cle: 'NPS', libelle: 'NPS', famille: 'CHAUD' },
  { cle: 'EN_EMPLOI', libelle: 'En emploi (à froid)', famille: 'FROID' },
];

const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

function lecturePoint(point: PointEvolution, mesure: Mesure): { valeur: number | null; part: number; texte: string; reponses: number } {
  if (mesure === 'MOYENNE') {
    const valeur = point.chaud.moyenne;
    return {
      valeur,
      part: valeur === null ? 0 : (valeur / 5) * 100,
      texte: valeur === null ? '—' : valeur.toFixed(1).replace('.', ','),
      reponses: point.chaud.reponses,
    };
  }
  if (mesure === 'NPS') {
    const valeur = point.chaud.nps;
    // De −100 à +100 : la barre part du bas de l'échelle, le chiffre dit le signe.
    return {
      valeur,
      part: valeur === null ? 0 : (valeur + 100) / 2,
      texte: texteNps(valeur),
      reponses: point.chaud.reponses,
    };
  }
  const valeur = point.froid.enEmploi;
  return {
    valeur,
    part: valeur ?? 0,
    texte: valeur === null ? '—' : `${valeur} %`,
    reponses: point.froid.reponses,
  };
}

function Evolution({ donnees }: { donnees: DonneesTableau }): JSX.Element {
  const disponibles = MESURES.filter((mesure) => donnees.familles.includes(mesure.famille));
  const [choisie, setChoisie] = useState<Mesure>('MOYENNE');
  const mesure = disponibles.some((candidate) => candidate.cle === choisie) ? choisie : (disponibles[0]?.cle ?? 'MOYENNE');
  const libelle = MESURES.find((candidate) => candidate.cle === mesure)?.libelle ?? '';
  // Quand le graphique défile, il s'ouvre sur le mois le plus récent.
  const defilement = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const zone = defilement.current;
    if (zone) zone.scrollLeft = zone.scrollWidth;
  }, [donnees.evolution]);

  return (
    <Carte
      titre="Évolution mois par mois"
      actions={
        <div className="flex flex-wrap gap-1" role="group" aria-label="Mesure de l’évolution">
          {disponibles.map((candidate) => (
            <button
              key={candidate.cle}
              type="button"
              onClick={() => setChoisie(candidate.cle)}
              aria-pressed={mesure === candidate.cle}
              className={`min-h-[36px] rounded-lg px-3 text-xs font-semibold transition [@media(pointer:coarse)]:min-h-[40px] ${
                mesure === candidate.cle ? 'bg-codialis-navy text-white' : 'text-codialis-gray hover:bg-gray-100'
              }`}
            >
              {candidate.libelle}
            </button>
          ))}
        </div>
      }
      sansBordure
    >
      <div className="p-4 sm:p-6">
        {/* Au téléphone, trente-six mois ne tiennent pas : le graphique défile dans sa carte. */}
        <div ref={defilement} className="-mx-1 overflow-x-auto px-1 pb-1">
          <ol className="flex h-48 min-w-full items-stretch gap-1.5" aria-label={`${libelle}, mois par mois`}>
            {donnees.evolution.map((point) => {
              const lu = lecturePoint(point, mesure);
              const [annee, rang] = point.mois.split('-').map(Number) as [number, number];
              return (
                <li key={point.mois} className="flex min-w-[2.25rem] flex-1 flex-col items-center gap-1">
                  <span className="sr-only">
                    {point.libelle} : {lu.valeur === null ? 'aucune réponse' : lu.texte}, {pluriel(lu.reponses, 'réponse')}
                  </span>
                  <span className="text-[10px] font-bold text-codialis-navy" aria-hidden>
                    {lu.texte}
                  </span>
                  <div className="flex w-full flex-1 items-end justify-center rounded-md bg-gray-50" aria-hidden>
                    {lu.valeur !== null && (
                      <div
                        className="w-3/4 max-w-[2rem] rounded-t-md bg-gradient-to-t from-codialis-blue to-codialis-sky"
                        style={{ height: `${Math.max(3, lu.part)}%` }}
                      />
                    )}
                  </div>
                  <span className="text-center text-[10px] leading-tight text-codialis-gray" aria-hidden>
                    {MOIS_COURTS[rang - 1]}
                    <br />
                    {String(annee).slice(2)} · {lu.reponses}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
        <p className="mt-3 text-[11px] text-codialis-gray">
          Sous chaque mois : l’année et le nombre de réponses reçues ce mois-là. Un mois sans réponse reste vide.
        </p>
      </div>
    </Carte>
  );
}

// ---------------------------------------------------------------------------
// Par formation, formateur, promotion
// ---------------------------------------------------------------------------

function ValeurGroupe({ libelle, valeur, detail }: { libelle: string; valeur: string; detail?: JSX.Element | string | null }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wider text-codialis-gray">{libelle}</div>
      <div className="text-sm font-semibold text-codialis-navy">{valeur}</div>
      {detail && <div className="text-[11px] text-codialis-gray">{detail}</div>}
    </div>
  );
}

function texteTaux(retour: { envoyes: number; repondus: number; tauxReponse: number | null }): string {
  return retour.envoyes === 0 ? '—' : `${enPourcent(retour.tauxReponse)} (${retour.repondus}/${retour.envoyes})`;
}

function ParAxe({ donnees }: { donnees: DonneesTableau }): JSX.Element {
  const axes = donnees.options.axes.filter(
    (axe) => axe.valeur !== 'FORMATEUR' || donnees.lecture.perimetre !== 'REFERENT',
  );
  const [choisi, setChoisi] = useState<AxeSatisfaction>('FORMATION');
  const axe = axes.some((candidat) => candidat.valeur === choisi) ? choisi : 'FORMATION';
  const lignes = donnees.groupes[axe];
  const litChaud = donnees.familles.includes('CHAUD');
  const litFroid = donnees.familles.includes('FROID');

  return (
    <Carte
      titre={`Résultats par ${(axes.find((candidat) => candidat.valeur === axe)?.libelle ?? '').toLowerCase()}`}
      actions={
        <div className="flex flex-wrap gap-1" role="group" aria-label="Regroupement">
          {axes.map((candidat) => (
            <button
              key={candidat.valeur}
              type="button"
              onClick={() => setChoisi(candidat.valeur)}
              aria-pressed={axe === candidat.valeur}
              className={`min-h-[36px] rounded-lg px-3 text-xs font-semibold transition [@media(pointer:coarse)]:min-h-[40px] ${
                axe === candidat.valeur ? 'bg-codialis-navy text-white' : 'text-codialis-gray hover:bg-gray-100'
              }`}
            >
              {candidat.libelle}
            </button>
          ))}
        </div>
      }
      sansBordure
    >
      {lignes.length === 0 ? (
        <p className="p-4 text-sm text-codialis-gray sm:p-6">Aucun questionnaire envoyé ni répondu sur la période.</p>
      ) : (
        <>
          {/* Sous l'ordinateur, une carte par groupe : le tableau y défilerait de côté. */}
          <ul className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0 xl:hidden">
            {lignes.map((ligne) => (
              <li key={ligne.cle} className="space-y-3 border-gray-100 p-4 md:border-b md:odd:border-r">
                <NomGroupe ligne={ligne} />
                <div className="grid grid-cols-2 gap-3">
                  {litChaud && (
                    <>
                      <ValeurGroupe
                        libelle="À chaud"
                        valeur={surCinq(ligne.chaud.moyenne)}
                        detail={
                          <span className="inline-flex flex-wrap items-center gap-1">
                            {pluriel(ligne.chaud.reponses, 'réponse')}
                            <EcartPastille ecart={ligne.chaud.ecart} unite="note" />
                          </span>
                        }
                      />
                      <ValeurGroupe
                        libelle="NPS"
                        valeur={texteNps(ligne.chaud.nps.score)}
                        detail={ligne.chaud.nps.repondants > 0 ? pluriel(ligne.chaud.nps.repondants, 'note') : null}
                      />
                      <ValeurGroupe libelle="Taux de réponse" valeur={texteTaux(ligne.chaud)} />
                    </>
                  )}
                  {litFroid && (
                    <>
                      <ValeurGroupe
                        libelle="À froid · en emploi"
                        valeur={enPourcent(ligne.froid.enEmploi.part)}
                        detail={pluriel(ligne.froid.reponses, 'réponse')}
                      />
                      <ValeurGroupe libelle="Emploi en lien" valeur={enPourcent(ligne.froid.emploiEnLien.part)} />
                      <ValeurGroupe libelle="Diplôme obtenu" valeur={enPourcent(ligne.froid.diplomeObtenu.part)} />
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="relative hidden overflow-x-auto xl:block">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {[
                    LIBELLES_COLONNE_AXE[axe],
                    ...(litChaud ? ['Réponses à chaud', 'Moyenne', 'NPS', 'Taux de réponse'] : []),
                    ...(litFroid ? ['Réponses à froid', 'En emploi', 'Emploi en lien', 'Diplôme obtenu'] : []),
                  ].map((entete) => (
                    <th
                      key={entete}
                      scope="col"
                      className="px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                    >
                      {entete}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lignes.map((ligne) => (
                  <tr key={ligne.cle} className="align-top">
                    <td className="px-4 py-3">
                      <NomGroupe ligne={ligne} />
                    </td>
                    {litChaud && (
                      <>
                        <td className="px-4 py-3 text-codialis-navy">{ligne.chaud.reponses}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="whitespace-nowrap font-semibold text-codialis-navy">{surCinq(ligne.chaud.moyenne)}</span>
                            <EcartPastille ecart={ligne.chaud.ecart} unite="note" />
                          </div>
                          {ligne.chaud.moyenne !== null && <Etoiles valeur={ligne.chaud.moyenne} taille="h-3 w-3" />}
                        </td>
                        <td className="px-4 py-3 text-codialis-navy">
                          <div className="font-semibold">{texteNps(ligne.chaud.nps.score)}</div>
                          {ligne.chaud.nps.repondants > 0 && (
                            <div className="text-[11px] text-codialis-gray">{pluriel(ligne.chaud.nps.repondants, 'note')}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-codialis-navy">{texteTaux(ligne.chaud)}</td>
                      </>
                    )}
                    {litFroid && (
                      <>
                        <td className="px-4 py-3 text-codialis-navy">{ligne.froid.reponses}</td>
                        <td className="px-4 py-3 text-codialis-navy">{enPourcent(ligne.froid.enEmploi.part)}</td>
                        <td className="px-4 py-3 text-codialis-navy">{enPourcent(ligne.froid.emploiEnLien.part)}</td>
                        <td className="px-4 py-3 text-codialis-navy">{enPourcent(ligne.froid.diplomeObtenu.part)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {axe === 'FORMATEUR' && (
        <p className="border-t border-gray-100 px-4 py-3 text-[11px] text-codialis-gray sm:px-6">
          Une réponse compte chez le formateur référent actuel du dossier.
        </p>
      )}
    </Carte>
  );
}

const LIBELLES_COLONNE_AXE: Record<AxeSatisfaction, string> = {
  FORMATION: 'Formation',
  FORMATEUR: 'Formateur référent',
  PROMOTION: 'Promotion',
};

function NomGroupe({ ligne }: { ligne: LigneGroupeSatisfaction }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <span className={`min-w-0 break-words font-semibold ${ligne.cle === 'SANS' ? 'text-codialis-gray' : 'text-codialis-navy'}`}>
        {ligne.libelle}
      </span>
      {ligne.siteCode && <PastilleSite site={ligne.siteCode} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Commentaires
// ---------------------------------------------------------------------------

function bordureCommentaire(commentaire: CommentaireSatisfaction): string {
  if (commentaire.moyenne === null) return 'border-codialis-sky';
  if (commentaire.moyenne >= 4) return 'border-codialis-mint';
  if (commentaire.moyenne >= 3) return 'border-codialis-sky';
  return 'border-amber-400';
}

function Commentaires({ donnees }: { donnees: DonneesTableau }): JSX.Element {
  const [saisie, setSaisie] = useState('');
  const [famille, setFamille] = useState<'' | FamilleQuestionnaire>('');
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const recherche = cleRecherche(useValeurDifferee(saisie.trim(), 250));

  const filtres = useMemo(
    () =>
      donnees.commentaires.filter(
        (commentaire) =>
          (!famille || commentaire.famille === famille) &&
          (!recherche ||
            cleRecherche(
              [
                commentaire.texte,
                commentaire.question,
                commentaire.apprenant ?? '',
                commentaire.formation,
                commentaire.formateur,
                commentaire.promotion,
                commentaire.libelleType,
              ].join(' '),
            ).includes(recherche)),
      ),
    [donnees.commentaires, famille, recherche],
  );
  useEffect(() => {
    setPage(1);
  }, [donnees.commentaires, famille, recherche, taillePage]);
  const pages = Math.max(1, Math.ceil(filtres.length / taillePage));
  const affiches = filtres.slice((page - 1) * taillePage, page * taillePage);

  return (
    <Carte
      titre="Commentaires"
      actions={<span className="text-xs text-codialis-gray">{pluriel(donnees.commentaires.length, 'commentaire')}</span>}
      sansBordure
    >
      <div className="flex flex-col gap-2 border-b border-gray-100 p-3 sm:flex-row sm:flex-wrap sm:items-center sm:px-6">
        <ChampRecherche
          valeur={saisie}
          onChange={setSaisie}
          etiquette="Rechercher dans les commentaires"
          placeholder="Rechercher un mot, une formation…"
        />
        {donnees.familles.length > 1 && (
          <ChampFiltre valeur={famille} onChange={(valeur) => setFamille(valeur as '' | FamilleQuestionnaire)} etiquette="Famille de questionnaires">
            <option value="">À chaud et à froid</option>
            <option value="CHAUD">À chaud</option>
            <option value="FROID">À froid</option>
          </ChampFiltre>
        )}
        {!donnees.lecture.nominatif && (
          <span className="text-[11px] text-codialis-gray sm:ml-auto">Commentaires présentés sans le nom de leur auteur.</span>
        )}
      </div>

      {filtres.length === 0 ? (
        <p className="flex items-center gap-2 p-4 text-sm text-codialis-gray sm:p-6">
          <MessageSquareText className="h-4 w-4 shrink-0" aria-hidden />
          {donnees.commentaires.length === 0
            ? 'Aucun commentaire laissé sur la période.'
            : 'Aucun commentaire ne correspond à la recherche.'}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {affiches.map((commentaire) => (
            <li key={commentaire.id} className="px-4 py-3.5 sm:px-6">
              <div className={`min-w-0 border-l-4 py-1 pl-4 ${bordureCommentaire(commentaire)}`}>
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  {commentaire.moyenne !== null && (
                    <>
                      <Etoiles valeur={commentaire.moyenne} taille="h-3 w-3" />
                      <span className="text-xs font-bold text-codialis-navy">{surCinq(commentaire.moyenne)}</span>
                    </>
                  )}
                  <Pastille texte={commentaire.libelleType} ton={commentaire.famille === 'CHAUD' ? 'info' : 'neutral'} />
                  {commentaire.nps !== null && (
                    <Pastille
                      texte={`Recommandation ${commentaire.nps}/10`}
                      ton={commentaire.nps >= 9 ? 'success' : commentaire.nps >= 7 ? 'neutral' : 'warning'}
                    />
                  )}
                  {commentaire.recommande !== null && (
                    <Pastille
                      texte={commentaire.recommande ? 'Recommande' : 'Ne recommande pas'}
                      ton={commentaire.recommande ? 'success' : 'warning'}
                    />
                  )}
                  {commentaire.situation && <Pastille texte={commentaire.situation} ton="neutral" />}
                </div>
                <p className="break-words text-sm italic text-codialis-navy">« {commentaire.texte} »</p>
                <div className="mt-1 text-xs text-codialis-gray">
                  {[
                    commentaire.apprenant,
                    commentaire.formation,
                    commentaire.formateur,
                    `répondu le ${jourFrancais(commentaire.repondLe)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  <span className="ml-2 inline-block align-middle">
                    <PastilleSite site={commentaire.siteCode} />
                  </span>
                </div>
                <div className="text-[11px] text-codialis-gray/80">{commentaire.question}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {filtres.length > 0 && (
        <Pagination
          page={page}
          pages={pages}
          total={filtres.length}
          affiches={affiches.length}
          taillePage={taillePage}
          onPage={setPage}
          onChangerTaille={setTaillePage}
          uniteLabel="commentaire"
        />
      )}
    </Carte>
  );
}
